// Oike's central base · «Equipo» (Ale's supervision of the commercial team), computed from the records Ale already
// loads (pure, tested by scripts/test-commercial-team.mjs). Nothing here reads, writes or decides an assignment:
// it shows who works each company (Accounts › BDR, one Users record), what each person has pending (the same queue
// as Seguimiento, src/followupQueue.js) and the assignments Ale has to resolve (several responsibles, an older way
// of assigning, a responsible whose email does not identify one Users record). Nothing is chosen for her.
import { teamAssignmentOf, teamUserOf, linkedIdsOf, ASSIGNMENT_HISTORY_FIELD } from './commercialScope.js';
import { pendingReplies, focusItems, outreachByContact } from './followupQueue.js';
import { outreachKind } from './outreachEvidence.js';

export const DUE_TAGS = ['urgent', 'followup'];
const F = (r, k) => { const v = r?.fields?.[k]; return v && typeof v === 'object' && !Array.isArray(v) ? (v.name ?? '') : (v ?? ''); };

/** Accounts › Assignment History → events, newest first. A line that is not valid JSON is kept as text, never dropped. */
export function assignmentHistoryOf(text) {
  return String(text || '').split('\n').map(l => l.trim()).filter(Boolean).map(l => {
    try { const e = JSON.parse(l); return e && typeof e === 'object' ? e : { raw: l }; } catch { return { raw: l }; }
  });
}

/** Users records that can work for the team: a unique email identifies them. */
export function teamCandidates(users = []) {
  return users.map(u => {
    const who = teamUserOf(users, F(u, 'Email'));
    return { id: u.id, name: F(u, 'Name') || F(u, 'Email') || u.id, email: String(F(u, 'Email') || ''), identifiable: who.status === 'ok' && who.record.id === u.id, problem: who.status === 'ok' ? (who.record.id === u.id ? '' : 'ambiguous') : who.status };
  });
}

/** The companies assigned to someone of the team other than Ale (by her email): they leave her own Hoy list. */
export function delegatedAccountIds({ accounts = [], users = [], ownerEmail = '' } = {}) {
  const owner = String(ownerEmail || '').trim().toLowerCase();
  const out = new Set();
  for (const a of accounts) {
    const t = teamAssignmentOf(a, users);
    if (t.state !== 'assigned') continue;
    const u = users.find(x => x.id === t.userId);
    if (u && String(F(u, 'Email')).trim().toLowerCase() !== owner) out.add(a.id);
  }
  return out;
}

/**
 * The supervision view: { members: [{ user, accounts, totals }], toResolve: [{ account, state, reason, ... }], unassigned }.
 * Each assigned company carries its contacts, the answers to give and the follow-ups that are due, and its last activity
 * (date, who registered it and what kind: a channel opened never counts as a contact).
 */
export function teamOverview({ accounts = [], users = [], stakeholders = [], outreach = [], campaigns = [], now = Date.now() } = {}) {
  const people = teamCandidates(users);
  const byContact = outreachByContact(outreach);
  const members = new Map();
  const toResolve = [];
  const unassigned = [];
  const contactsOf = (accId) => stakeholders.filter(s => linkedIdsOf(s, 'Account').includes(accId));
  for (const a of accounts) {
    const t = teamAssignmentOf(a, users);
    const base = { id: a.id, name: F(a, 'Account Name') || '(sin nombre)', history: assignmentHistoryOf(F(a, ASSIGNMENT_HISTORY_FIELD)), bdr: linkedIdsOf(a, 'BDR') };
    if (t.state === 'none') { unassigned.push(base); continue; }
    if (t.state === 'multiple') { toResolve.push({ ...base, state: 'multiple', who: t.userIds.map(id => people.find(p => p.id === id)?.name || 'ficha que ya no existe') }); continue; }
    if (t.state === 'legacy') { toResolve.push({ ...base, state: 'legacy', who: [t.ownerText, ...t.userIds.map(id => people.find(p => p.id === id)?.name || '')].filter(Boolean) }); continue; }
    const person = people.find(p => p.id === t.userId);
    if (!person) { toResolve.push({ ...base, state: 'missing_user', who: [] }); continue; }
    const contacts = contactsOf(a.id);
    const ids = new Set(contacts.map(c => c.id));
    const replies = pendingReplies({ stakeholders: contacts, byContact, now });
    const followups = focusItems({ stakeholders: contacts, byContact, accounts, campaigns, now }).filter(f => DUE_TAGS.includes(f.tag));
    const acts = outreach.filter(o => linkedIdsOf(o, 'Account').includes(a.id) || linkedIdsOf(o, 'Stakeholder').some(id => ids.has(id)))
      .sort((x, y) => new Date(F(y, 'Date') || 0) - new Date(F(x, 'Date') || 0));
    const last = acts[0] || null;
    const row = {
      ...base, contacts: contacts.length,
      replies: replies.map(r => ({ id: r.s.id, name: `${F(r.s, 'Name')} ${F(r.s, 'Last name')}`.trim(), daysSince: r.daysSince })),
      followups: followups.map(f => ({ id: f.s.id, name: `${F(f.s, 'Name')} ${F(f.s, 'Last name')}`.trim(), daysSince: f.e?.daysSince ?? null, tag: f.tag })),
      last: last ? { date: F(last, 'Date') || null, by: F(last, 'Logged By') || '', kind: outreachKind(last) } : null,
    };
    if (!person.identifiable) toResolve.push({ ...base, state: 'unidentifiable', who: [person.name], problem: person.problem });
    if (!members.has(person.id)) members.set(person.id, { user: person, accounts: [] });
    members.get(person.id).accounts.push(row);
  }
  const list = [...members.values()].map(m => ({
    ...m,
    accounts: m.accounts.sort((x, y) => (y.replies.length + y.followups.length) - (x.replies.length + x.followups.length) || x.name.localeCompare(y.name, 'es')),
    totals: { accounts: m.accounts.length, replies: m.accounts.reduce((n, a) => n + a.replies.length, 0), followups: m.accounts.reduce((n, a) => n + a.followups.length, 0) },
  })).sort((x, y) => x.user.name.localeCompare(y.user.name, 'es'));
  return { members: list, toResolve, unassigned: unassigned.sort((x, y) => x.name.localeCompare(y.name, 'es')) };
}
